import React, { useState } from 'react';

import { ModelBase } from "@models";
import { DataType, SourceBookType } from "@dndModels/Types";

import MyTTRPGAPI from "@utilities/smart_api";

import FlexBox from '@components/layout/FlexBox';
import SelectSourceBookBox from "@dndComponents/select/SelectSourceBookBox";
import DandDButtonBox from './DandDButtonBoxSimple';

const api = MyTTRPGAPI.getInstance();

interface Props {
  data_type: DataType;
  plural: string;
  objects: ModelBase[]; // Whatever the index is currently showing (after its filters).
  onDone: () => void;
}

type Mode = "Add" | "Remove";

// Admin tool: adds a source book to (or removes one from) every object the index is showing, keeping their other books.
export default function AddSourceBookTool({ data_type, plural, objects, onDone }: Props) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<Mode>("Add");
  const [book_id, setBookId] = useState("");
  const [book_type, setBookType] = useState<SourceBookType | "">("");
  const [confirm, setConfirm] = useState(false);
  const [saving, setSaving] = useState<{ done: number, total: number, failed: string[] } | null>(null);

  const adding = mode === "Add";
  const book = api.source_books.find(o => o._id === book_id);
  const book_name = book ? (book.abbreviation || book.name) : "";
  const targets = objects.filter(o => o.source_book_ids.includes(book_id) !== adding);
  const running = !!saving && saving.done < saving.total;
  const to_from = adding ? "to" : "from";

  const applyToAll = async () => {
    setConfirm(false);
    const list = [...targets];
    const failed: string[] = [];
    setSaving({ done: 0, total: list.length, failed });
    for (let i = 0; i < list.length; ++i) {
      const obj = list[i];
      try {
        // The index shows the light copies, so also keep anything that's only on the full doc.
        const full = await api.getObjectFromAPI<ModelBase>({ data_type, id: obj._id });
        const existing = [...obj.source_books_old, ...(full ? full.source_books_old : [])];
        let source_books = existing.filter((o, idx) => existing.findIndex(o2 => o2.id === o.id) === idx).map(o => ({ ...o }));
        if (adding) {
          if (!source_books.some(o => o.id === book_id)) {
            source_books.push({ id: book_id, type: book ? book.source_book_type : "Source Book", third_party: book ? book.third_party : false });
          }
        } else {
          source_books = source_books.filter(o => o.id !== book_id);
        }
        if (await api.updateSourceBooks(data_type, obj, source_books)) {
          obj.source_books = api.source_books.filter(sb => source_books.some(o => o.id === sb._id));
        } else {
          failed.push(obj.name);
        }
      } catch (ex) {
        console.error(ex);
        failed.push(obj.name);
      }
      setSaving({ done: i + 1, total: list.length, failed: [...failed] });
    }
    onDone();
  };

  const openAs = (m: Mode) => {
    setMode(m);
    setConfirm(false);
    setSaving(null);
    setOpen(true);
  };

  if (!open) {
    return (
      <FlexBox direction="row">
        <FlexBox xs_expand={0}>
          <DandDButtonBox name="Add Source Book to Shown" onClick={() => openAs("Add")} />
        </FlexBox>
        <FlexBox xs_expand={0}>
          <DandDButtonBox name="Remove Source Book from Shown" onClick={() => openAs("Remove")} />
        </FlexBox>
      </FlexBox>
    );
  }
  return (
    <FlexBox direction="column" className="padded" style={{ border: "1px solid rgba(128,128,128,0.5)", borderRadius: 4 }}>
      <span className="header">{ mode } Source Book { to_from } Shown { plural }</span>
      <span>
        Use the filters below to narrow down the list. { adding ?
          `This adds the book to all of the ${plural.toLowerCase()} shown, and keeps the books they already have.` :
          `This removes the book from all of the ${plural.toLowerCase()} shown, and keeps their other books.`
        }
      </span>
      <SelectSourceBookBox name={mode} id={book_id} type={book_type} disabled={running || confirm}
        onChange={(type, id) => { setBookType(type); setBookId(id); setSaving(null); }}
      />
      { confirm ?
        <FlexBox direction="row">
          <FlexBox>
            <span>{ mode } { book_name } { to_from } { targets.length } { plural }?</span>
          </FlexBox>
          <FlexBox xs_expand={0}>
            <DandDButtonBox name={`Yes, ${mode} ${to_from} ${targets.length}`} onClick={applyToAll} />
          </FlexBox>
          <FlexBox xs_expand={0}>
            <DandDButtonBox name="Cancel" onClick={() => setConfirm(false)} />
          </FlexBox>
        </FlexBox>
      :
        <FlexBox direction="row">
          <FlexBox xs_expand={0}>
            <DandDButtonBox name={book_id === "" ? "Choose a Source Book" : `${mode} ${book_name} ${to_from} ${targets.length} Shown`}
              disabled={running || book_id === "" || targets.length === 0}
              onClick={() => setConfirm(true)}
            />
          </FlexBox>
          <FlexBox xs_expand={0}>
            <DandDButtonBox name={adding ? "Switch to Remove" : "Switch to Add"} disabled={running}
              onClick={() => { setMode(adding ? "Remove" : "Add"); setSaving(null); }}
            />
          </FlexBox>
          <FlexBox xs_expand={0}>
            <DandDButtonBox name="Close" disabled={running} onClick={() => { setOpen(false); setSaving(null); }} />
          </FlexBox>
        </FlexBox>
      }
      { book_id !== "" && objects.length > targets.length &&
        <span style={{ opacity: 0.7 }}>
          { objects.length - targets.length } shown { adding ? "already have" : "don't have" } { book_name }.
        </span>
      }
      { saving &&
        <span>
          { running ?
            `${adding ? "Adding" : "Removing"} ${book_name} ${saving.done} / ${saving.total}...` :
            `${adding ? "Added" : "Removed"} ${book_name} ${to_from} ${saving.total - saving.failed.length} of ${saving.total}.${saving.failed.length > 0 ? ` Failed: ${saving.failed.join(", ")}` : ""}`
          }
        </span>
      }
    </FlexBox>
  );
}
